<template>
    <Head title="Incident Notification Policies" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Incident Notification Policies</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                Define who gets notified when incidents are reported based on type, severity, and location
                            </p>
                        </div>
                        <InfoButton title="Managing Notification Policies" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Incident Notification Policies page is where you configure automated email notifications
                                that are sent when incidents are reported. Each notification policy defines matching criteria
                                (incident type, severity, location, near miss filter) and specifies who should receive
                                notifications (specific users and/or role-based groups). Well-designed notification policies
                                ensure the right people are immediately aware of incidents requiring their attention, enable
                                rapid response to critical situations, support accountability by automatically informing
                                stakeholders, and reduce delays caused by manual notification processes. Notification policies
                                work alongside SLA policies to create a comprehensive incident response framework.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Search:</strong> Quickly find notification policies by name using the search box<br>
                                • <strong>Status Filter:</strong> Toggle between viewing All policies, Active Only, or Inactive
                                  Only to focus on specific policy states<br>
                                • <strong>Reset Filters:</strong> Clear all search and filter criteria to view the full list<br>
                                • <strong>Add Notification Policy:</strong> Click to create a new automated notification rule
                                  with matching criteria and recipient configuration<br>
                                • <strong>Table View:</strong> See all notification policies with their matching criteria
                                  (incident type, severity, location, near miss filter), recipient counts (users and roles),
                                  and active/inactive status<br>
                                • <strong>Matching Criteria Column:</strong> Shows what incidents trigger this policy - "All
                                  incidents" means no filters applied, otherwise displays specific type/severity/location filters<br>
                                • <strong>Recipients Column:</strong> Displays count of individual users and roles that will
                                  receive notifications when matching incidents are reported<br>
                                • <strong>View/Edit:</strong> Click to review full policy details including all recipients and
                                  matching criteria, or make changes<br>
                                • <strong>Activate/Deactivate:</strong> Toggle policy status - inactive policies don't send
                                  notifications but are preserved for historical reference<br>
                                • <strong>Delete:</strong> Permanently remove notification policies that are no longer needed
                                  (use with caution - cannot be undone)<br>
                                • <strong>Pagination:</strong> Navigate through multiple pages if you have many notification
                                  policies configured
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Start with critical incidents</strong> - Create notification policies for
                                  high-severity and critical incidents first to ensure urgent situations get immediate attention<br>
                                • <strong>Use role-based recipients</strong> - Notify roles (like "Safety Manager" or "Facility
                                  Manager") rather than individual users so policies don't break when staff changes<br>
                                • <strong>Avoid notification overload</strong> - Too many notifications cause people to ignore
                                  them - be selective about who needs to know what<br>
                                • <strong>Match policies to responsibilities</strong> - Only notify people who have actual
                                  responsibility or need-to-know for that incident type<br>
                                • <strong>Consider location-based routing</strong> - Route notifications to managers responsible
                                  for specific locations rather than sending everything to everyone<br>
                                • <strong>Test before activating</strong> - Create policies as inactive, review the configuration,
                                  then activate when ready<br>
                                • <strong>Use specific criteria strategically</strong> - Broad criteria (no filters) notify for
                                  all incidents, specific criteria (type + severity + location) target notifications precisely<br>
                                • <strong>Combine with SLA policies</strong> - Notification policies alert people, SLA policies
                                  enforce response timeframes - use both together<br>
                                • <strong>Monitor recipient lists</strong> - Periodically review who's receiving notifications
                                  and ensure the lists are still relevant<br>
                                • <strong>Deactivate instead of delete</strong> - Keep historical notification policies inactive
                                  rather than deleting to maintain audit trail<br>
                                • <strong>Document your strategy</strong> - Use descriptive policy names and descriptions that
                                  explain the notification routing logic<br>
                                • <strong>Consider escalation patterns</strong> - Create tiered notification policies where
                                  initial notifications go to responders and escalation notifications go to leadership<br>
                                • <strong>Account for after-hours coverage</strong> - Ensure 24/7 incidents have notification
                                  policies that reach on-call personnel<br>
                                • <strong>Near miss notifications matter</strong> - Don't neglect near miss notifications -
                                  they're often early warnings of serious incidents to come<br>
                                • Changes to notification policies apply immediately - newly reported incidents will use current
                                  active notification policies
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.incidents.notification-policies.create')"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Notification Policy
                    </Link>
                </div>
            </div>

            <!-- Filters -->
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow mb-6 p-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Search -->
                    <div>
                        <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                        <input
                            id="search"
                            v-model="filters.search"
                            type="text"
                            placeholder="Search by name..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            @input="debouncedSearch"
                        />
                    </div>

                    <!-- Active Filter -->
                    <div>
                        <label for="active" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <select
                            id="active"
                            v-model="filters.active"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            @change="search"
                        >
                            <option value="">All</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </select>
                    </div>

                    <!-- Reset Button -->
                    <div class="flex items-end">
                        <button
                            @click="resetFilters"
                            class="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            <!-- Policies Table -->
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Policy Name
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Matching Criteria
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Recipients
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th scope="col" class="relative px-6 py-3">
                                <span class="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-if="policies.data.length === 0">
                            <td colspan="5" class="px-6 py-12 text-center">
                                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No notification policies</h3>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new notification policy.</p>
                            </td>
                        </tr>
                        <tr v-for="policy in policies.data" :key="policy.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td class="px-6 py-4">
                                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ policy.name }}</div>
                                <div v-if="policy.description" class="text-sm text-gray-500 dark:text-gray-400">{{ policy.description }}</div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="text-sm text-gray-900 dark:text-white">
                                    <div v-if="policy.incident_type" class="mb-1">
                                        <span class="font-medium">Type:</span> {{ policy.incident_type.name }}
                                    </div>
                                    <div v-if="policy.severity" class="mb-1">
                                        <span class="font-medium">Severity:</span>
                                        <span :class="getSeverityClass(policy.severity)" class="ml-1 px-2 py-0.5 text-xs rounded-full">
                                            {{ policy.severity }}
                                        </span>
                                    </div>
                                    <div v-if="policy.location" class="mb-1">
                                        <span class="font-medium">Location:</span> {{ policy.location.name }}
                                    </div>
                                    <div v-if="policy.near_miss_only" class="mb-1">
                                        <span class="px-2 py-0.5 text-xs rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">Near Miss Only</span>
                                    </div>
                                    <div v-if="!policy.incident_type && !policy.severity && !policy.location && !policy.near_miss_only" class="text-gray-500 dark:text-gray-400 italic">
                                        All incidents
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                <div v-if="policy.notify_users && policy.notify_users.length > 0" class="mb-1">
                                    <span class="font-medium">Users:</span> {{ policy.notify_users.length }}
                                </div>
                                <div v-if="policy.notify_roles && policy.notify_roles.length > 0">
                                    <span class="font-medium">Roles:</span> {{ policy.notify_roles.length }}
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span v-if="policy.is_active" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                    Active
                                </span>
                                <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                    Inactive
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <Link
                                    :href="route('settings.incidents.notification-policies.edit', policy.id)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                                >
                                    View/Edit
                                </Link>
                                <button
                                    @click="toggleStatus(policy.id)"
                                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mr-4"
                                >
                                    {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                                </button>
                                <button
                                    @click="confirmDelete(policy)"
                                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Pagination -->
                <div v-if="policies.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    <div class="flex items-center justify-between">
                        <div class="flex-1 flex justify-between sm:hidden">
                            <button
                                @click="changePage(policies.current_page - 1)"
                                :disabled="policies.current_page === 1"
                                class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                @click="changePage(policies.current_page + 1)"
                                :disabled="policies.current_page === policies.last_page"
                                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    Showing
                                    <span class="font-medium">{{ policies.from }}</span>
                                    to
                                    <span class="font-medium">{{ policies.to }}</span>
                                    of
                                    <span class="font-medium">{{ policies.total }}</span>
                                    results
                                </p>
                            </div>
                            <div>
                                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        @click="changePage(policies.current_page - 1)"
                                        :disabled="policies.current_page === 1"
                                        class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span class="sr-only">Previous</span>
                                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                    <template v-for="page in paginationPages" :key="page">
                                        <button
                                            v-if="page !== '...'"
                                            @click="changePage(page)"
                                            :class="[
                                                page === policies.current_page
                                                    ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400'
                                                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600',
                                                'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                        <span
                                            v-else
                                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            ...
                                        </span>
                                    </template>
                                    <button
                                        @click="changePage(policies.current_page + 1)"
                                        :disabled="policies.current_page === policies.last_page"
                                        class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span class="sr-only">Next</span>
                                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policies: {
        type: Object,
        required: true,
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const filters = reactive({
    search: props.filters.search || '',
    active: props.filters.active || '',
});

let debounceTimer = null;

const debouncedSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        search();
    }, 500);
};

const search = () => {
    router.get(route('settings.incidents.notification-policies.index'), filters, {
        preserveState: true,
        preserveScroll: true,
    });
};

const resetFilters = () => {
    filters.search = '';
    filters.active = '';
    search();
};

const changePage = (page) => {
    router.get(route('settings.incidents.notification-policies.index'), {
        ...filters,
        page: page,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const getSeverityClass = (severity) => {
    const classes = {
        low: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
        medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        critical: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[severity] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
};

const toggleStatus = (policyId) => {
    router.post(route('settings.incidents.notification-policies.toggle-status', policyId), {}, {
        preserveScroll: true,
    });
};

const confirmDelete = (policy) => {
    if (confirm(`Are you sure you want to delete "${policy.name}"? This action cannot be undone.`)) {
        router.delete(route('settings.incidents.notification-policies.destroy', policy.id), {
            preserveScroll: true,
        });
    }
};

// Generate pagination page numbers with ellipsis
const paginationPages = computed(() => {
    const current = props.policies.current_page;
    const last = props.policies.last_page;
    const delta = 2; // Number of pages to show on each side of current page
    const pages = [];

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let rangeStart = Math.max(2, current - delta);
    let rangeEnd = Math.min(last - 1, current + delta);

    // Add ellipsis after first page if needed
    if (rangeStart > 2) {
        pages.push('...');
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (rangeEnd < last - 1) {
        pages.push('...');
    }

    // Always show last page (if more than 1 page)
    if (last > 1) {
        pages.push(last);
    }

    return pages;
});
</script>
